/**
 * SubAreaPicker - Wrapper around generic EntityPicker
 * 
 * Provides sub-area-specific configuration and maintains backward compatibility
 * with any code that imports SubAreaPickerField or SubAreaPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── SubArea-specific configuration ───────────────────────────────────────────
const SUBAREA_CONFIG = {
	apiEndpoint: "/api/SubArea/active",
	entityName: "Sub Area",
	idField: "id",
	displayFields: {
		primary: "description",
		secondary: "code"
	},
	tableHeaders: ["Code", "Description"],
	tableAccessors: ["code", "description"],
	searchFields: [
		{ label: "All", field: "all", index: 0 },
		{ label: "Description", field: "description", index: 1 },
		{ label: "Code", field: "code", index: 2 },
	],
	placeholder: "--Select sub-area--"
};

// ── SubAreaPickerField wrapper ───────────────────────────────────────────────
/**
 * SubAreaPickerField
 * 
 * Usage:
 * <SubAreaPickerField
 *   label="Sub Area"
 *   subAreaId={form.subAreaId}
 *   displayValue={form.subAreaDescription}
 *   onSelect={(subArea) => {
 *     set("subAreaId", subArea.id);
 *     set("subAreaCode", subArea.code);
 *     set("subAreaDescription", subArea.description);
 *   }}
 *   onClear={() => {
 *     set("subAreaId", "");
 *     set("subAreaCode", "");
 *     set("subAreaDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function SubAreaPickerField({
	subAreaId,          // subarea-specific prop name
	displayValue = "",
	onSelect,
	onClear,
	...rest             // label, isRequired, error, disabled, etc.
}) {
	return (
		<EntityPickerField
			{...rest}
			config={SUBAREA_CONFIG}
			entityId={subAreaId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── SubAreaPickerModal wrapper ───────────────────────────────────────────────
/**
 * SubAreaPickerModal
 * 
 * Usage (standalone):
 * <SubAreaPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(subArea) => {
 *     set("subAreaId", subArea.id);
 *     set("subAreaDescription", subArea.description);
 *   }}
 * />
 */
export function SubAreaPickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={SUBAREA_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { SubAreaPickerField as default };